import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { PaymentPlanCard, StyledTooltip } from '@DesignSystem/data-display';
import { SimpleTable } from '@DesignSystem/tables';
import { GroupButton } from '@ds';
import useCurrentUser from '@API/auth/useCurrentUser';

import {
  PAYMENT_PLANS,
  ORGANIZATION_PLAN_STARTER,
  PAID_PLANS,
  ORGANIZATION_PLAN_FREE,
  ORGANIZATION_PLAN_ENTERPRISE,
  FREE_PLANS,
  APPLICATION_TYPE,
  ORGANIZATION_PLAN_PRO_SPANS,
  PLANS_WITH_TRIAL
} from '@shared/constants';
import {
  EXPERIMENT_MANAGEMENT_COMPARE_PLANS_COLUMNS,
  EXPERIMENT_MANAGEMENT_COMPARE_PLANS_SECONDARY_COLUMNS,
  EXPERIMENT_MANAGEMENT_COMPARE_PLANS_ROWS,
  LLM_EVALUATION_COMPARE_PLANS_COLUMNS,
  LLM_EVALUATION_COMPARE_PLANS_ROWS,
  MPM_COMPARE_PLANS_COLUMNS,
  MPM_COMPARE_PLANS_ROWS,
  PLATFORM_COMPARE_PLANS_COLUMNS,
  PLATFORM_COMPARE_PLANS_ROWS,
  LLM_MONTHLY_USAGE_PLANS_COLUMNS,
  LLM_MONTHLY_USAGE_PLANS_ROWS
} from '../helpers';
import { useBetaFeatureEnabled, useCurrentPaymentPlan } from '@shared/hooks';
import { useActiveOrganizationPaymentDetails } from '@account-settings/api';
import {
  BETA_FEATURE_LLM_EVAL,
  BETA_FEATURE_NEW_PAYMENT_PLANS
} from '@/lib/betaFeatures';

const COLUMN_WIDTHS_THREE_PLANS = [
  {
    width: '40%',
    key: 'feature'
  },
  {
    width: '20%',
    key: 'free'
  },
  {
    width: '20%',
    key: 'starter'
  }
];

const NEW_PAYMENT_PLANS = [
  ORGANIZATION_PLAN_FREE,
  ORGANIZATION_PLAN_PRO_SPANS,
  ORGANIZATION_PLAN_ENTERPRISE
];

const OLD_PAYMENT_PLANS = [
  ORGANIZATION_PLAN_FREE,
  ORGANIZATION_PLAN_STARTER,
  ORGANIZATION_PLAN_ENTERPRISE
];

const SelectPaymentPlan = ({
  selectedPlan,
  onChangeSelectedPlan,
  onClickTrialPlan
}) => {
  const [userPlatform, setUserPlatform] = useState(APPLICATION_TYPE.em);
  const { data: currentUser } = useCurrentUser();
  const { data: billingDetails } = useActiveOrganizationPaymentDetails();
  const isLLM = userPlatform === APPLICATION_TYPE.llm;
  const isEM = userPlatform === APPLICATION_TYPE.em;

  const {
    paymentPlanName: activePaymentPlan,
    isAcademic,
    isStartupFree,
    isTrial
  } = useCurrentPaymentPlan();

  const areNewPaymentPlansEnabled = useBetaFeatureEnabled(
    BETA_FEATURE_NEW_PAYMENT_PLANS
  );

  useEffect(() => {
    if (currentUser?.userAppType === APPLICATION_TYPE.llm) {
      setUserPlatform(APPLICATION_TYPE.llm);
    } else {
      setUserPlatform(APPLICATION_TYPE.em);
    }
  }, [currentUser]);

  const isLLMEvalEnabled = useBetaFeatureEnabled(BETA_FEATURE_LLM_EVAL);

  const isOneAdminRestLLMUsers =
    billingDetails?.adminMembersCount === 1 &&
    billingDetails?.llmUserCount === billingDetails?.allMembersCount - 1;

  const allDisplayedPaymentPlans = useMemo(() => {
    if (areNewPaymentPlansEnabled) {
      return NEW_PAYMENT_PLANS;
    }

    return OLD_PAYMENT_PLANS;
  }, [areNewPaymentPlansEnabled]);

  const MOST_POPULAR = useMemo(() => {
    if (areNewPaymentPlansEnabled) {
      return [ORGANIZATION_PLAN_PRO_SPANS];
    }

    return [ORGANIZATION_PLAN_STARTER];
  }, [areNewPaymentPlansEnabled]);

  const displayedPaymentPlans = useMemo(() => {
    let resultList = [...allDisplayedPaymentPlans];

    if (isAcademic) {
      return [activePaymentPlan];
    }

    if (!allDisplayedPaymentPlans.includes(activePaymentPlan)) {
      resultList = [
        allDisplayedPaymentPlans[0],
        activePaymentPlan,
        allDisplayedPaymentPlans[1],
        allDisplayedPaymentPlans[2]
      ];
    }

    return resultList;
  }, [allDisplayedPaymentPlans, activePaymentPlan, isAcademic]);

  const panelHasTrialButton = displayedPaymentPlans.some(
    displayedPaymentPlan =>
      PLANS_WITH_TRIAL.includes(displayedPaymentPlan) &&
      FREE_PLANS.includes(activePaymentPlan) &&
      !isAcademic &&
      !isStartupFree
  );

  const isSmallPaymentPlan = displayedPaymentPlans?.length > 3;

  const renderCards = () => (
    <div>
      <div className="buttons-wrapper">
        <GroupButton
          selectedValue={userPlatform}
          onValueChange={setUserPlatform}
          size={'medium'}
        >
          <GroupButton.Item value={APPLICATION_TYPE.llm}>
            Opik - LLM evaluation
          </GroupButton.Item>
          <GroupButton.Item value={APPLICATION_TYPE.em}>
            MLOps platform
          </GroupButton.Item>
        </GroupButton>
      </div>
      <div className="payment-plans">
        {displayedPaymentPlans.map(displayedPaymentPlan => {
          const paymentPlan = PAYMENT_PLANS[displayedPaymentPlan];
          const IS_COMMUNITY_DISABLED =
            displayedPaymentPlan === ORGANIZATION_PLAN_FREE &&
            PAID_PLANS.includes(activePaymentPlan) &&
            billingDetails?.allMembersCount > 1 &&
            !isOneAdminRestLLMUsers;

          const IS_ALLOWED_PAYMENT_PLAN = allDisplayedPaymentPlans.includes(
            displayedPaymentPlan
          );

          return (
            <div
              key={paymentPlan?.plan}
              className={cx('payment-plan-container', {
                small: isSmallPaymentPlan
              })}
            >
              <StyledTooltip
                title={
                  IS_COMMUNITY_DISABLED
                    ? 'Only organizations with one member \n can be downgraded to the free plan'
                    : ''
                }
                placement="top"
              >
                <span>
                  <PaymentPlanCard
                    activeCard={selectedPlan === displayedPaymentPlan}
                    isActivePaymentPlan={
                      activePaymentPlan === displayedPaymentPlan
                    }
                    color={paymentPlan?.color}
                    paymentBullets={
                      isLLM && paymentPlan?.paymentBulletsLLM
                        ? paymentPlan?.paymentBulletsLLM
                        : paymentPlan?.paymentBullets
                    }
                    bullets={
                      isLLM && paymentPlan?.bulletsLLM
                        ? paymentPlan?.bulletsLLM
                        : paymentPlan?.bullets
                    }
                    bulletsTitle={paymentPlan?.bulletsTitle}
                    plan={paymentPlan?.plan}
                    planBenefits={paymentPlan?.planBenefits}
                    isAcademic={
                      isAcademic &&
                      displayedPaymentPlan !== ORGANIZATION_PLAN_FREE
                    }
                    label={paymentPlan?.label}
                    priceDescription={
                      isLLM && paymentPlan?.priceDescriptionLLM
                        ? paymentPlan?.priceDescriptionLLM
                        : paymentPlan?.priceDescription
                    }
                    priceLabel={paymentPlan?.priceLabel}
                    title={paymentPlan?.name}
                    showTrialButton={
                      PLANS_WITH_TRIAL.includes(displayedPaymentPlan) &&
                      FREE_PLANS.includes(activePaymentPlan) &&
                      !isAcademic &&
                      !isStartupFree
                    }
                    onTrialClick={() => onClickTrialPlan(displayedPaymentPlan)}
                    onClick={() => onChangeSelectedPlan(displayedPaymentPlan)}
                    disabled={IS_COMMUNITY_DISABLED || !IS_ALLOWED_PAYMENT_PLAN}
                    badgeLabel={
                      MOST_POPULAR.includes(displayedPaymentPlan)
                        ? 'Popular'
                        : null
                    }
                    isEM={isEM}
                    panelHasTrialButton={panelHasTrialButton}
                    isTrial={isTrial}
                  />
                </span>
              </StyledTooltip>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderComparePlans = () => {
    return (
      <div className="payment-plans-grid">
        <div className="full-width-modal-title">
          <span>Compare Plans</span>
        </div>
        {!isLLM && (
          <SimpleTable
            columns={EXPERIMENT_MANAGEMENT_COMPARE_PLANS_COLUMNS}
            secondaryColumns={
              EXPERIMENT_MANAGEMENT_COMPARE_PLANS_SECONDARY_COLUMNS
            }
            rows={EXPERIMENT_MANAGEMENT_COMPARE_PLANS_ROWS}
            columnWidths={COLUMN_WIDTHS_THREE_PLANS}
          />
        )}

        {isLLMEvalEnabled && (
          <SimpleTable
            columns={LLM_EVALUATION_COMPARE_PLANS_COLUMNS}
            secondaryColumns={
              isLLM
                ? EXPERIMENT_MANAGEMENT_COMPARE_PLANS_SECONDARY_COLUMNS
                : null
            }
            rows={LLM_EVALUATION_COMPARE_PLANS_ROWS}
            columnWidths={COLUMN_WIDTHS_THREE_PLANS}
          />
        )}

        {isLLM && (
          <SimpleTable
            columns={LLM_MONTHLY_USAGE_PLANS_COLUMNS}
            rows={LLM_MONTHLY_USAGE_PLANS_ROWS}
            columnWidths={COLUMN_WIDTHS_THREE_PLANS}
          />
        )}

        {!isLLM && (
          <SimpleTable
            columns={MPM_COMPARE_PLANS_COLUMNS}
            rows={MPM_COMPARE_PLANS_ROWS}
            columnWidths={COLUMN_WIDTHS_THREE_PLANS}
          />
        )}

        <SimpleTable
          columns={PLATFORM_COMPARE_PLANS_COLUMNS}
          rows={PLATFORM_COMPARE_PLANS_ROWS}
          columnWidths={COLUMN_WIDTHS_THREE_PLANS}
        />
      </div>
    );
  };
  return (
    <>
      {renderCards()}
      {renderComparePlans()}
    </>
  );
};

SelectPaymentPlan.propTypes = {
  selectedPlan: PropTypes.string.isRequired,
  onChangeSelectedPlan: PropTypes.func.isRequired,
  onClickTrialPlan: PropTypes.func.isRequired
};

export default SelectPaymentPlan;
